import React, { useState } from 'react';
import { MessageSquareIcon, EyeIcon, UserIcon, UsersIcon, GlobeIcon, LockIcon } from 'lucide-react';
export type NoteVisibility = 'Public' | 'Internal' | 'Role-based' | 'User-specific' | 'Creator-only';
interface StandardizedNotesProps {
  value: string;
  onChange: (value: string) => void;
  visibility?: NoteVisibility;
  onVisibilityChange?: (visibility: NoteVisibility) => void;
  visibilityDetails?: string;
  onVisibilityDetailsChange?: (details: string) => void;
  placeholder?: string;
  label?: string;
  roles?: {
    id: string;
    name: string;
  }[];
  users?: {
    id: string;
    name: string;
  }[];
  className?: string;
  rows?: number;
  disabled?: boolean;
  required?: boolean;
}
export function StandardizedNotes({
  value,
  onChange,
  visibility = 'Internal',
  onVisibilityChange,
  visibilityDetails = '',
  onVisibilityDetailsChange,
  placeholder = 'Add notes here...',
  label = 'Notes',
  roles = [],
  users = [],
  className = '',
  rows = 3,
  disabled = false,
  required = false
}: StandardizedNotesProps) {
  const [showVisibilityOptions, setShowVisibilityOptions] = useState(false);
  // Default roles if none provided
  const defaultRoles = [{
    id: 'finance',
    name: 'Finance Team'
  }, {
    id: 'procurement',
    name: 'Procurement Team'
  }, {
    id: 'management',
    name: 'Management'
  }, {
    id: 'auditor',
    name: 'Auditors'
  }];
  // Default users if none provided
  const defaultUsers = [{
    id: 'user-001',
    name: 'John Smith'
  }, {
    id: 'user-002',
    name: 'Jane Doe'
  }, {
    id: 'user-003',
    name: 'Robert Brown'
  }];
  const actualRoles = roles.length > 0 ? roles : defaultRoles;
  const actualUsers = users.length > 0 ? users : defaultUsers;
  const getVisibilityIcon = () => {
    switch (visibility) {
      case 'Public':
        return <GlobeIcon className="h-4 w-4 text-green-500" data-id="element-2050" />;
      case 'Internal':
        return <UsersIcon className="h-4 w-4 text-blue-500" data-id="element-2051" />;
      case 'Role-based':
        return <UsersIcon className="h-4 w-4 text-purple-500" data-id="element-2052" />;
      case 'User-specific':
        return <UserIcon className="h-4 w-4 text-orange-500" data-id="element-2053" />;
      case 'Creator-only':
        return <LockIcon className="h-4 w-4 text-red-500" data-id="element-2054" />;
      default:
        return <EyeIcon className="h-4 w-4 text-gray-500" data-id="element-2055" />;
    }
  };
  const getVisibilityLabel = () => {
    switch (visibility) {
      case 'Public':
        return 'Public - Visible to everyone including external users';
      case 'Internal':
        return 'Internal - Visible to all internal users';
      case 'Role-based':
        return 'Role-based - Visible only to specific roles';
      case 'User-specific':
        return 'User-specific - Visible only to specific users';
      case 'Creator-only':
        return 'Private - Visible only to you';
      default:
        return 'Select visibility';
    }
  };
  return <div className={`${className}`} data-id="element-2056">
      <div className="flex justify-between items-center mb-1" data-id="element-2057">
        <label className="block text-sm font-medium text-gray-700" data-id="element-2058">
          {label} {required && <span className="text-red-500" data-id="element-2059">*</span>}
        </label>
        {onVisibilityChange && <div className="relative" data-id="element-2060">
            <button type="button" onClick={() => setShowVisibilityOptions(!showVisibilityOptions)} className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50" data-id="element-2061">
              {getVisibilityIcon()}
              <span className="ml-1" data-id="element-2062">Visibility</span>
            </button>
            {showVisibilityOptions && <div className="absolute right-0 mt-1 w-64 bg-white rounded-md shadow-lg z-10 border border-gray-200" data-id="element-2063">
                <div className="py-1" data-id="element-2064">
                  <div className="px-3 py-2 text-xs font-medium text-gray-500 border-b border-gray-200" data-id="element-2065">
                    Select who can see this note
                  </div>
                  <button type="button" className={`flex items-center w-full text-left px-4 py-2 text-sm ${visibility === 'Public' ? 'bg-gray-100' : 'hover:bg-gray-50'}`} onClick={() => {
              onVisibilityChange('Public');
              setShowVisibilityOptions(false);
            }} data-id="element-2066">
                    <GlobeIcon className="h-4 w-4 mr-2 text-green-500" data-id="element-2067" />
                    Public
                    <span className="ml-1 text-xs text-gray-500" data-id="element-2068">
                      (Everyone)
                    </span>
                  </button>
                  <button type="button" className={`flex items-center w-full text-left px-4 py-2 text-sm ${visibility === 'Internal' ? 'bg-gray-100' : 'hover:bg-gray-50'}`} onClick={() => {
              onVisibilityChange('Internal');
              setShowVisibilityOptions(false);
            }} data-id="element-2069">
                    <UsersIcon className="h-4 w-4 mr-2 text-blue-500" data-id="element-2070" />
                    Internal
                    <span className="ml-1 text-xs text-gray-500" data-id="element-2071">
                      (All staff)
                    </span>
                  </button>
                  <button type="button" className={`flex items-center w-full text-left px-4 py-2 text-sm ${visibility === 'Role-based' ? 'bg-gray-100' : 'hover:bg-gray-50'}`} onClick={() => {
              onVisibilityChange('Role-based');
              setShowVisibilityOptions(false);
            }} data-id="element-2072">
                    <UsersIcon className="h-4 w-4 mr-2 text-purple-500" data-id="element-2073" />
                    Role-based
                    <span className="ml-1 text-xs text-gray-500" data-id="element-2074">
                      (Specific roles)
                    </span>
                  </button>
                  <button type="button" className={`flex items-center w-full text-left px-4 py-2 text-sm ${visibility === 'User-specific' ? 'bg-gray-100' : 'hover:bg-gray-50'}`} onClick={() => {
              onVisibilityChange('User-specific');
              setShowVisibilityOptions(false);
            }} data-id="element-2075">
                    <UserIcon className="h-4 w-4 mr-2 text-orange-500" data-id="element-2076" />
                    User-specific
                    <span className="ml-1 text-xs text-gray-500" data-id="element-2077">
                      (Select users)
                    </span>
                  </button>
                  <button type="button" className={`flex items-center w-full text-left px-4 py-2 text-sm ${visibility === 'Creator-only' ? 'bg-gray-100' : 'hover:bg-gray-50'}`} onClick={() => {
              onVisibilityChange('Creator-only');
              setShowVisibilityOptions(false);
            }} data-id="element-2078">
                    <LockIcon className="h-4 w-4 mr-2 text-red-500" data-id="element-2079" />
                    Private
                    <span className="ml-1 text-xs text-gray-500" data-id="element-2080">
                      (Only you)
                    </span>
                  </button>
                </div>
              </div>}
          </div>}
      </div>
      {/* Visibility indicator */}
      {onVisibilityChange && <div className="flex items-center mb-2 text-xs text-gray-500" data-id="element-2081">
          {getVisibilityIcon()}
          <span className="ml-1" data-id="element-2082">{getVisibilityLabel()}</span>
        </div>}
      {/* Role or User selection when applicable */}
      {onVisibilityDetailsChange && visibility === 'Role-based' && <div className="mb-2" data-id="element-2083">
          <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-2084">
            Select roles who can view this note:
          </label>
          <select className="block w-full py-1 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500" value={visibilityDetails} onChange={e => onVisibilityDetailsChange(e.target.value)} multiple size={3} data-id="element-2085">
            {actualRoles.map(role => <option key={role.id} value={role.id} data-id="element-2086">
                {role.name}
              </option>)}
          </select>
        </div>}
      {onVisibilityDetailsChange && visibility === 'User-specific' && <div className="mb-2" data-id="element-2087">
          <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-2088">
            Select users who can view this note:
          </label>
          <select className="block w-full py-1 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500" value={visibilityDetails} onChange={e => onVisibilityDetailsChange(e.target.value)} multiple size={3} data-id="element-2089">
            {actualUsers.map(user => <option key={user.id} value={user.id} data-id="element-2090">
                {user.name}
              </option>)}
          </select>
        </div>}
      {/* Notes textarea */}
      <div className="relative" data-id="element-2091">
        <div className="absolute top-2 left-2 text-gray-400" data-id="element-2092">
          <MessageSquareIcon className="h-5 w-5" data-id="element-2093" />
        </div>
        <textarea value={value} onChange={e => onChange(e.target.value)} rows={rows} className="block w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500" placeholder={placeholder} disabled={disabled} data-id="element-2094" />
      </div>
    </div>;
}